import type { QuotationSupplierProduct } from 'apis/restaurant-inquiries';
import { Badge, Box, ChakraSelect, Flex, Input, Text } from 'design-system';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import {
  availableTiers,
  formatDerivedPrices,
  ladderOf,
  type PriceTier,
} from 'pages/inquiry-processor/utils/price-units';
import { supplierPriceOf } from 'pages/inquiry-processor/utils/quotable';
import { fmt } from 'pages/inquiry-processor/utils/scoring';
import type { ReactNode } from 'react';

// Compact price line for suggestion cards and search options: the supplier's
// live price for the master product (Prices screen), green as the "priced"
// signal (red is the app-wide primary accent, not a price marker).
export const ProductPriceTag = ({ product }: { product: QuotationSupplierProduct }) => {
  const price = supplierPriceOf(product);
  if (price) {
    const inactive = price.isActive === false;
    return (
      <Flex align='center' gap='1' whiteSpace='nowrap'>
        <Badge colorScheme={inactive ? 'red' : 'green'} variant='subtle' fontSize='2xs'>
          {inactive ? 'PRICE · inactive' : 'PRICE'}
        </Badge>
        <Text fontSize='xs' fontWeight='semibold' color={inactive ? 'red.600' : 'green.600'}>
          {price.dealPrice} AED
        </Text>
      </Flex>
    );
  }
  return (
    <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
      No price from this supplier
    </Text>
  );
};

interface PriceLadderProps {
  product: QuotationSupplierProduct;
  price: string;
  onPriceChange: (v: string) => void;
  // Which rung of the product's packaging ladder the negotiated price is
  // agreed against. Null only while the backend serializes no ladder at all,
  // where the unit control hides and this behaves as it always did.
  priceUnit?: PriceTier | null;
  onPriceUnitChange?: (tier: PriceTier) => void;
  targetPrice?: number | null; // the item's expectedPrice, as a reference
  extra?: ReactNode; // e.g. the quote-history clock button
  addDeal?: ReactNode; // inline price creation, shown in the Supplier price slot when there is none
}

// Supplier price → Negotiated. The supplier's live price (Prices screen) is the
// read-only reference; Negotiated is the single editable field (red bottom
// accent = "type here" — the project-wide convention from the old pricing cell).
//
// The negotiated figure is meaningless without the rung it was agreed on: for a
// 1 carton × 6 pieces × 2 kg product, 198 is either 198/carton or 198/kg — a
// 12× difference. So the input carries a unit picker listing only the rungs the
// product's master actually defines, and a live preview restating the number on
// the others, so the per-kg consequence is visible while typing rather than
// discovered after saving.
export const PriceLadder = ({
  product,
  price,
  onPriceChange,
  priceUnit,
  onPriceUnitChange,
  targetPrice,
  extra,
  addDeal,
}: PriceLadderProps) => {
  const ladder = ladderOf(product);
  const unitOptions = availableTiers(ladder).map(({ tier, unit }) => ({ value: tier, label: unit }));
  const selectedUnit = unitOptions.find((option) => option.value === priceUnit) ?? null;
  const priceNumber = Number(price);
  const derived =
    priceUnit && Number.isFinite(priceNumber) && priceNumber > 0
      ? formatDerivedPrices(priceNumber, priceUnit, ladder)
      : null;
  const deal = supplierPriceOf(product);
  const dealAt = deal?.lastModifiedAt ? new Date(deal.lastModifiedAt).getTime() : 0;
  return (
    <Flex align='flex-start' gap='5' wrap='wrap'>
      <Box>
        <Text fontSize='10px' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
          Supplier price
        </Text>
        {deal ? (
          <>
            <Flex align='center' gap='1'>
              <Text fontSize='sm' fontWeight='semibold' color={deal.isActive === false ? 'red.600' : 'green.600'}>
                {deal.dealPrice} AED
              </Text>
              {deal.isActive === false && (
                <Badge colorScheme='red' variant='subtle' fontSize='2xs'>
                  inactive
                </Badge>
              )}
            </Flex>
            {dealAt ? (
              <Text fontSize='xs' color='gray.500'>
                modified {fmt(dealAt)}
              </Text>
            ) : null}
          </>
        ) : (
          addDeal ?? (
            <Text fontSize='sm' color='gray.400'>
              —
            </Text>
          )
        )}
      </Box>
      <Box flex='1' minW='200px'>
        <Text fontSize='10px' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
          Negotiated{targetPrice != null ? ` · target ${targetPrice} AED` : ''}
        </Text>
        <Flex gap='1' align='center'>
          <Input
            size='sm'
            value={price}
            onChange={(e) => onPriceChange(numOnly(e.target.value))}
            placeholder='Negotiated price'
            borderBottomColor='red.500'
            _hover={{ borderBottomColor: 'red.500' }}
          />
          {/* Hidden entirely when the backend serializes no ladder — there is
              no honest unit to offer, so the field stays as it always was. */}
          {unitOptions.length > 0 && onPriceUnitChange && (
            <Box w='130px' flexShrink={0}>
              <ChakraSelect
                options={unitOptions}
                value={selectedUnit}
                onChange={(option: any) => option?.value && onPriceUnitChange(option.value as PriceTier)}
                placeholder='per…'
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition='fixed'
                styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
              />
            </Box>
          )}
          {extra}
        </Flex>
        {/* Absent when nothing is derivable (a baseUnit-only master, or a pack
            size nobody recorded) — an empty row would imply a missing value
            rather than an unanswerable question. */}
        {derived && (
          <Text fontSize='xs' color='gray.500' mt='1'>
            ≈ {derived}
          </Text>
        )}
      </Box>
    </Flex>
  );
};
